/**
 * StepInsight.tsx  (Step 2)
 *
 * Shows the distilled insight and the generated LinkedIn post.
 * The user can switch the tone angle (which triggers a new generation) or
 * manually edit the post text. Confirmed with "Pick a template →".
 *
 * Pencil icon next to the raw thought opens an inline edit textarea so the
 * user can re-word their thought and re-distill without leaving this page.
 * Existing content stays visible until the new distill succeeds.
 */

import React, { useState } from "react";
import { ANGLES } from "./constants";
import type { AngleKey, DistillResult } from "./types";
import {
  card, primaryBtn, ghostBtn,
  stepHeading, stepSub, textarea,
  ACCENT, ACCENT_08,
} from "./sharedStyles";
import SlideCountPicker from "./SlideCountPicker";

// ── Props ─────────────────────────────────────────────────────────────────────

interface Props {
  thought:               string;
  distillResult:         DistillResult;
  activeAngle:           AngleKey;
  linkedinPost:          string;
  isEditing:             boolean;
  isGeneratingPost:      boolean;
  isDistilling:          boolean;
  isRevising:            boolean;
  preferredSlideCount:   number | null;
  setLinkedinPost:       (v: string) => void;
  setIsEditing:          (v: boolean | ((prev: boolean) => boolean)) => void;
  setPreferredSlideCount:(v: number | null) => void;
  onAngle:               (angle: AngleKey) => void;
  onNext:                () => void;
  /** Re-distill with an (optionally edited) thought. Old content stays until success. */
  onReDistill:           (newThought: string) => void;
  /** Apply a guided revision instruction to the current post. */
  onRevise:              (instruction: string) => Promise<void>;
  /** All previous instructions already applied for this angle, in order. */
  revisionHistory:       string[];
  /** Remove a specific instruction from the history by index. */
  onRemoveRevisionInstruction: (index: number) => void;
  /** Detected (or user-overridden) perspective for the current content. */
  perspective:           "first_person" | "observer";
  /** Toggle between first_person and observer — triggers a re-generation. */
  onTogglePerspective:   () => void;
}

// ── Local styles ──────────────────────────────────────────────────────────────

const insightLabel: React.CSSProperties = {
  fontSize:      "10px",
  fontWeight:    700,
  letterSpacing: "0.08em",
  textTransform: "uppercase",
  color:         ACCENT,
  marginBottom:  "8px",
};

const insightText: React.CSSProperties = {
  fontSize:    "15px",
  lineHeight:  "1.6",
  color:       "#0A0A0A",
  fontStyle:   "italic",
  borderLeft:  `3px solid ${ACCENT}`,
  paddingLeft: "12px",
  margin:      0,
};

const angleBtn = (active: boolean, loading: boolean): React.CSSProperties => ({
  padding:      "6px 14px",
  borderRadius: "100px",
  border:       active ? `1.5px solid ${ACCENT}` : "1.5px solid #E4E4E0",
  background:   active ? ACCENT_08 : "#fff",
  color:        active ? ACCENT : "#888",
  fontSize:     "12px",
  fontWeight:   600,
  cursor:       loading ? "not-allowed" : "pointer",
  transition:   "all 0.15s",
  opacity:      loading && !active ? 0.5 : 1,
});

// ── Component ─────────────────────────────────────────────────────────────────

const StepInsight: React.FC<Props> = ({
  thought, distillResult, activeAngle, linkedinPost,
  isEditing, isGeneratingPost, isDistilling, isRevising,
  preferredSlideCount,
  setLinkedinPost, setIsEditing, setPreferredSlideCount,
  onAngle, onNext, onReDistill, onRevise,
  revisionHistory, onRemoveRevisionInstruction,
  perspective, onTogglePerspective,
}) => {
  const [editingThought,       setEditingThought]       = useState(false);
  const [draftThought,         setDraftThought]         = useState("");
  const [revisionInstruction,  setRevisionInstruction]  = useState("");

  const openThoughtEdit = () => {
    setDraftThought(thought);
    setEditingThought(true);
  };

  const cancelThoughtEdit = () => setEditingThought(false);

  const submitThoughtEdit = () => {
    if (draftThought.trim() && !isDistilling) {
      onReDistill(draftThought.trim());
      setEditingThought(false);
    }
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "20px" }}>
      <div>
        <p style={stepHeading}>Your insight + post</p>
        <p style={stepSub}>Pick an angle to reshape the post, or edit it yourself.</p>
      </div>

      {/* ── Raw thought — read-only or inline edit ── */}
      <div style={{ display: "flex", alignItems: "flex-start", gap: "10px" }}>
        <span style={{
          fontSize:      "12px", fontWeight: 700, color: "#bbb",
          letterSpacing: "0.06em", textTransform: "uppercase",
          paddingTop:    "2px", flexShrink: 0,
        }}>You</span>

        {editingThought ? (
          /* Inline edit mode */
          <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: "8px" }}>
            <textarea
              autoFocus
              value={draftThought}
              onChange={e => setDraftThought(e.target.value)}
              onKeyDown={e => {
                if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); submitThoughtEdit(); }
                if (e.key === "Escape") cancelThoughtEdit();
              }}
              style={{
                ...textarea,
                fontSize:     "14px",
                fontStyle:    "italic",
                color:        "#444",
                border:       "1.5px solid #E4E4E0",
                borderRadius: "8px",
                padding:      "8px 10px",
                minHeight:    "72px",
                resize:       "vertical",
              }}
            />
            <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
              <button
                style={{
                  ...primaryBtn,
                  fontSize:  "12px",
                  padding:   "7px 14px",
                  opacity:   isDistilling || !draftThought.trim() ? 0.5 : 1,
                  cursor:    isDistilling || !draftThought.trim() ? "not-allowed" : "pointer",
                }}
                onClick={submitThoughtEdit}
                disabled={isDistilling || !draftThought.trim()}
              >
                {isDistilling ? "Distilling…" : "Re-distill →"}
              </button>
              <button
                style={{ ...ghostBtn, fontSize: "12px", padding: "7px 12px" }}
                onClick={cancelThoughtEdit}
                disabled={isDistilling}
              >
                Cancel
              </button>
            </div>
          </div>
        ) : (
          /* Read-only mode */
          <>
            <p style={{ fontSize: "14px", color: "#888", lineHeight: "1.6", margin: 0, fontStyle: "italic", flex: 1 }}>
              "{thought}"
            </p>
            <button
              onClick={openThoughtEdit}
              title="Edit your prompt"
              style={{
                background:   "none",
                border:       "none",
                cursor:       "pointer",
                padding:      "2px 4px",
                flexShrink:   0,
                color:        "#ccc",
                lineHeight:   1,
                borderRadius: "4px",
                transition:   "color 0.15s",
                display:      "flex",
                alignItems:   "center",
              }}
              onMouseEnter={e => { (e.currentTarget as HTMLButtonElement).style.color = "#888"; }}
              onMouseLeave={e => { (e.currentTarget as HTMLButtonElement).style.color = "#ccc"; }}
            >
              {/* Tilted pencil — universal "edit" icon */}
              <svg width="13" height="13" viewBox="0 0 24 24" fill="currentColor">
                <path d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25zM20.71 7.04a1 1 0 0 0 0-1.41l-2.34-2.34a1 1 0 0 0-1.41 0l-1.83 1.83 3.75 3.75 1.83-1.83z"/>
              </svg>
            </button>
          </>
        )}
      </div>

      {/* Distilled insight card */}
      <div style={card}>
        <p style={insightLabel}>Distilled insight</p>
        <p style={insightText}>{distillResult.distilledInsight}</p>
      </div>

      {/* LinkedIn post card with angle switcher */}
      <div style={card}>
        <div style={{
          display: "flex", alignItems: "center",
          justifyContent: "space-between", marginBottom: "12px",
        }}>
          <p style={insightLabel}>LinkedIn post</p>
          <button
            onClick={() => setIsEditing(e => !e)}
            style={{
              fontSize: "11px", fontWeight: 600, color: "#888",
              background: "none", border: "1px solid #E4E4E0",
              borderRadius: "8px", padding: "3px 10px", cursor: "pointer",
            }}
          >
            {isEditing ? "Done" : "Edit"}
          </button>
        </div>

        {/* Perspective toggle — small row above angle selector */}
        <div style={{
          display:       "flex",
          alignItems:    "center",
          gap:           "8px",
          marginBottom:  "14px",
          paddingBottom: "12px",
          borderBottom:  "1px solid #F0F0EC",
        }}>
          <span style={{
            fontSize:      "10px",
            fontWeight:    700,
            letterSpacing: "0.07em",
            textTransform: "uppercase",
            color:         "#bbb",
            flexShrink:    0,
          }}>Voice</span>
          <div style={{ display: "flex", gap: "4px" }}>
            {([ ["first_person", "✦ My story"], ["observer", "◎ My take"] ] as const).map(([key, label]) => (
              <button
                key={key}
                onClick={() => { if (perspective !== key) onTogglePerspective(); }}
                disabled={isGeneratingPost}
                style={{
                  fontSize:     "11px",
                  fontWeight:   600,
                  padding:      "4px 10px",
                  borderRadius: "100px",
                  border:       perspective === key ? `1.5px solid ${ACCENT}` : "1.5px solid #E4E4E0",
                  background:   perspective === key ? ACCENT_08 : "#fff",
                  color:        perspective === key ? ACCENT : "#aaa",
                  cursor:       isGeneratingPost ? "not-allowed" : "pointer",
                  transition:   "all 0.15s",
                  opacity:      isGeneratingPost && perspective !== key ? 0.5 : 1,
                }}
              >{label}</button>
            ))}
          </div>
          {isGeneratingPost && (
            <span style={{ fontSize: "11px", color: "#bbb", marginLeft: "4px" }}>Rewriting…</span>
          )}
        </div>

        {/* Angle selector row */}
        <div style={{ display: "flex", flexWrap: "wrap", gap: "8px", marginBottom: "16px" }}>
          {ANGLES.map(a => (
            <button
              key={a.key}
              style={angleBtn(activeAngle === a.key, isGeneratingPost)}
              onClick={() => onAngle(a.key)}
              disabled={isGeneratingPost}
            >
              {a.label}
              {activeAngle === a.key && isGeneratingPost && (
                <span style={{ marginLeft: "4px", opacity: 0.6 }}>…</span>
              )}
            </button>
          ))}
        </div>

        {/* Post content — editable textarea or read-only display */}
        {isEditing ? (
          <textarea
            style={{
              ...textarea,
              minHeight: "160px",
              border: "1px solid #E4E4E0", borderRadius: "10px", padding: "12px",
            }}
            value={linkedinPost}
            onChange={e => setLinkedinPost(e.target.value)}
          />
        ) : (
          <div style={{
            fontSize:   "14px",
            lineHeight: "1.7",
            color:      "#222",
            whiteSpace: "pre-wrap",
            minHeight:  "80px",
            opacity:    isGeneratingPost || isDistilling ? 0.45 : 1,
            transition: "opacity 0.2s",
          }}>
            {linkedinPost || <span style={{ color: "#bbb" }}>Generating…</span>}
          </div>
        )}

        {/* ── Guided revision + history ── */}
        {linkedinPost && !isGeneratingPost && !isDistilling && (
          <>
            {/* Revision input row */}
            <div style={{
              marginTop:    "16px",
              paddingTop:   "14px",
              borderTop:    "1px solid #F0F0EC",
              display:      "flex",
              gap:          "8px",
              alignItems:   "flex-start",
            }}>
              <textarea
                placeholder={'Revise this post… e.g. "make it shorter", "add a stat", "less salesy"'}
                value={revisionInstruction}
                onChange={e => setRevisionInstruction(e.target.value)}
                onKeyDown={e => {
                  if (e.key === "Enter" && !e.shiftKey && revisionInstruction.trim()) {
                    e.preventDefault();
                    onRevise(revisionInstruction).then(() => setRevisionInstruction(""));
                  }
                }}
                disabled={isRevising}
                style={{
                  flex:         1,
                  fontSize:     "13px",
                  lineHeight:   "1.5",
                  color:        "#333",
                  background:   "#FAFAF8",
                  border:       "1px solid #E4E4E0",
                  borderRadius: "8px",
                  padding:      "8px 10px",
                  resize:       "none",
                  minHeight:    "36px",
                  maxHeight:    "80px",
                  outline:      "none",
                  fontFamily:   "inherit",
                  opacity:      isRevising ? 0.6 : 1,
                }}
                rows={1}
              />
              <button
                onClick={() => {
                  if (!revisionInstruction.trim() || isRevising) return;
                  onRevise(revisionInstruction).then(() => setRevisionInstruction(""));
                }}
                disabled={!revisionInstruction.trim() || isRevising}
                style={{
                  flexShrink:   0,
                  fontSize:     "12px",
                  fontWeight:   600,
                  color:        !revisionInstruction.trim() || isRevising ? "#bbb" : ACCENT,
                  background:   "none",
                  border:       `1px solid ${!revisionInstruction.trim() || isRevising ? "#E4E4E0" : ACCENT}`,
                  borderRadius: "8px",
                  padding:      "8px 12px",
                  cursor:       !revisionInstruction.trim() || isRevising ? "not-allowed" : "pointer",
                  whiteSpace:   "nowrap",
                  transition:   "all 0.15s",
                }}
              >
                {isRevising ? "Revising…" : "Revise →"}
              </button>
            </div>

            {/* Applied correction history — visible pills so user knows what's in memory */}
            {revisionHistory.length > 0 && (
              <div style={{ marginTop: "10px" }}>
                <div style={{
                  fontSize: "10px", fontWeight: 700, letterSpacing: "0.07em",
                  textTransform: "uppercase", color: "#aaa", marginBottom: "6px",
                }}>
                  Applied corrections
                </div>
                <div style={{ display: "flex", flexWrap: "wrap", gap: "6px" }}>
                  {revisionHistory.map((instr, i) => (
                    <div key={i} style={{
                      display:      "flex",
                      alignItems:   "center",
                      gap:          "4px",
                      background:   ACCENT_08,
                      borderRadius: "12px",
                      padding:      "4px 10px 4px 12px",
                      fontSize:     "12px",
                      color:        "#555",
                      maxWidth:     "100%",
                    }}>
                      <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: "260px" }}>
                        "{instr}"
                      </span>
                      <button
                        onClick={() => onRemoveRevisionInstruction(i)}
                        title="Remove this correction from memory"
                        style={{
                          flexShrink:  0,
                          background:  "none",
                          border:      "none",
                          cursor:      "pointer",
                          color:       "#bbb",
                          fontSize:    "14px",
                          lineHeight:  1,
                          padding:     "0 0 0 2px",
                        }}
                      >×</button>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Slide count preference — set here before choosing a template */}
      <SlideCountPicker value={preferredSlideCount} onChange={setPreferredSlideCount} />

      <div style={{ display: "flex", justifyContent: "flex-end" }}>
        <button style={primaryBtn} onClick={onNext}>
          Pick a template →
        </button>
      </div>
    </div>
  );
};

export default StepInsight;
